/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: helpers.js
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  26.09.22 4:44 pm.
 * Last modified: 26.09.22 4:44 pm.
 *
 * More information at: https://egt-digital.com/
 */

import { SoundSpriteDefinitions } from 'howler';
import CrashGameLabel from '../components/custom/CrashGameLabel';
import BellLinkJackpot from '../components/Jackpot/BellLink';
import HighCashJackpot from '../components/Jackpot/HighCash';
import InstantLinkJackpot from '../components/Jackpot/InstantLink';
import MysteryJackpot from '../components/Jackpot/Mystery';
import StaticImageBanner from '../components/Jackpot/StaticImageBanner';
import VipBellLinkJackpot from '../components/Jackpot/VipBellLink';
import VipHighCashJackpot from '../components/Jackpot/VipHighCash';
import Tournament from '../components/Tournament';
import {
    bgSectionColor,
    BrandedStaticImagesMap,
    BrandedTournamentImagesMap,
    sectionsWithBgImages,
    soundSprites,
    WEBPACK_HASH,
    X_RIDE_MP,
} from '../constants';
import IGameSettings from '../interfaces/IGameSettings';
import ISettings from '../interfaces/ISettings';

export const copyGameSetting = (game: IGameSettings): IGameSettings => {
    const gameDenominationsCopy = new Map<string, number[]>();
    game.denominations.forEach((value, key) => gameDenominationsCopy.set(key, [...value]));

    const gameCopy: IGameSettings = {
        ...game,
        banners: {
            lobby: {
                rows: game.banners.lobby.rows,
                cols: game.banners.lobby.cols,
            },
            section: {
                rows: game.banners.section.rows,
                cols: game.banners.section.cols,
            },
        },
        minDenomination: new Map<string, number>(game.minDenomination),
        denominations: gameDenominationsCopy,
        branding: game.branding,
    };

    return gameCopy;
};

export const normalize = (key: string, arr: any[]) => {
    return arr.reduce<any>((acc, curr) => {
        const keyVal: string = curr[key] as string;
        acc[keyVal] = curr;
        return acc;
    }, {});
};

export const getGamesMinDenomination = (games: IGameSettings[], currency: string): number =>
    games?.map<number>(game => game.minDenomination.get(currency) || 1).sort((a, b) => a - b)[0] || 1;

export const posTypes = {
    rectangular: { rows: 1, cols: 1 },
    vertical: { rows: 2, cols: 1 },
    horizontal: { rows: 1, cols: 2 },
};

export const possitionCoords = (posType: string): { cols: number; rows: number } => {
    type ObjectKey = keyof typeof posTypes;

    if (posTypes.hasOwnProperty(posType)) {
        return posTypes[posType as ObjectKey];
    }
    return { cols: 1, rows: 1 };
};

export const getBannerOrientation = ({
    rows,
    cols,
}: {
    rows: number;
    cols: number;
}): 'vertical' | 'horizontal' | 'rectangular' => (cols === 2 ? 'horizontal' : rows === 2 ? 'vertical' : 'rectangular');

const gamesCountFor = (forType: 'imageLobby' | 'imageSection') => (type: string) => (
    games: Array<{
        imageLobby: string;
        imageSection: string;
    }>,
): number => games.filter(game => game[forType] === type).length;

const gamesCountByType = gamesCountFor('imageLobby');
export const verticalGamesCount = gamesCountByType('vertical');
export const horizontalGamesCount = gamesCountByType('horizontal');

export const formatCurrencyValue = (value: number, units: number) => {
    const formatted = value / units;
    const rounded = Math.floor(value / units);
    let decimals = ((formatted - rounded) * units).toFixed(0);
    if (decimals.length === 1) {
        decimals = '0' + decimals;
    }

    return `${rounded.toLocaleString().replace(/,/g, ' ')}.${decimals}`;
};

export const performAnimationFrameResizableUpdate = (resizable: HTMLElement, discardVisibility?: boolean) => {
    const rendered = () => updateResizables(resizable, discardVisibility);
    const startRendering = () => {
        requestAnimationFrame(rendered);
    };
    // avoid timeout, but perform updateResizables(resizable) on the 2nd animation frame
    requestAnimationFrame(startRendering);
};

const updateResizableOnLoad = (resizable: HTMLElement, target: HTMLElement, discardVisibility?: boolean): any => () => {
    // @ts-ignore
    target.onload = null;
    resizable.style.display = 'unset';

    performAnimationFrameResizableUpdate(resizable, discardVisibility);
};

export const gridBlocksCount = (games: IGameSettings[]) => {
    return games.reduce((prev, curr) => {
        return prev + (curr.banners.lobby.cols > 1 || curr.banners.lobby.rows > 1 ? 1 : 0);
    }, games.length);
};

export const getTimeComponents = (date: Date) => {
    let hours: number | string = date.getHours();
    let minutes: number | string = date.getMinutes();

    if (hours < 10) {
        hours = '0' + hours;
    }

    if (minutes < 10) {
        minutes = '0' + minutes;
    }

    return { hours, minutes };
};

const isVisible = (elem: HTMLElement): boolean => {
    const rect: DOMRect = elem.getBoundingClientRect();
    const winW = window.innerWidth;
    const winH = window.innerHeight;
    if (rect.y > 0 && rect.x > 0) {
        if (rect.y < winH && rect.x < winW) {
            return true;
        }
    } else {
        if (rect.y + rect.height > 0 && rect.x + rect.width > 0) {
            return true;
        }
    }
    return false;
};

export const getResizableParent = (el: HTMLElement): HTMLElement | undefined => {
    if (el.classList.contains('resizable')) {
        return el;
    }
    if (!el.parentElement) {
        return undefined;
    }
    if (el.parentElement.classList.contains('resizable')) {
        return el.parentElement;
    } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        return getResizableParent(el.parentElement);
    }
};

export const updateResizables = (onlyResizable?: HTMLElement, discardVisibility?: boolean): void => {
    let resizables: any = [];
    if (!onlyResizable) {
        resizables = Array.from(document.querySelectorAll('.resizable'));
    } else {
        resizables.push(onlyResizable);
    }

    resizables.forEach((element: Element) => {
        const resizable = element as HTMLDivElement;

        const targetId = '' + resizable.getAttribute('data-target');
        const target = document.getElementById(targetId) as HTMLImageElement;
        if (!target) {
            return;
        }

        if (!target.complete && onlyResizable) {
            if (!target.onload) {
                target.onload = updateResizableOnLoad(resizable, target, discardVisibility);
            }
        } else if (resizable.style.display !== 'unset') {
            resizable.style.display = 'unset';
            performAnimationFrameResizableUpdate(resizable, discardVisibility);
        }

        if (!isVisible(target) && !discardVisibility) {
            return;
        }

        const parent = resizable.parentElement as HTMLDivElement;
        const parentW = parent.clientWidth;

        const targetW = target.clientWidth;
        const targetH = target.clientHeight;
        const fontSizePercent = resizable.getAttribute('data-font-size-percent');

        if (fontSizePercent) {
            resizable.style.fontSize = Math.floor((targetH / 100) * Number(fontSizePercent)) + 'px';
        }

        const maxPercent = Number(resizable.getAttribute('data-max-percent')) || 100;
        let currentScale = 1;
        const scale = resizable.style.transform.match(/\((.+?)\)/gm);
        if (scale) {
            currentScale = Number(
                scale
                    .pop()
                    ?.replace('(', '')
                    .replace(')', ''),
            );
            if (currentScale === 0) {
                currentScale = 1;
            }
        }

        const resizableW = resizable.getBoundingClientRect().width / currentScale;
        const resizableH = resizable.getBoundingClientRect().height / currentScale;

        const moveUpPercent = resizable.getAttribute('data-move-up-percent');
        let moveUp = 0;
        if (moveUpPercent) {
            moveUp = (targetH / 100) * Number(moveUpPercent);
        }

        let paddingLeft = 0;
        let paddingRight = 0;

        const paddingLeftPixels = resizable.getAttribute('data-padding-left-pixels');
        if (paddingLeftPixels) {
            paddingLeft = Number(paddingLeftPixels);
        }

        const paddingRightPixels = resizable.getAttribute('data-padding-right-pixels');
        if (paddingRightPixels) {
            paddingRight = Number(paddingRightPixels);
        }

        const askedW = targetW * (maxPercent / 100);
        let percentW = 1;

        if (resizableW > askedW - paddingLeft - paddingRight) {
            percentW = (askedW - paddingLeft - paddingRight) / resizableW;
            if (percentW >= 0) {
                resizable.style.transform = 'scale(' + percentW.toFixed(5) + ')';
            }
        } else {
            resizable.style.transform = 'unset';
        }

        const percentWDiff = 1 - percentW;

        let textAlign = resizable.getAttribute('data-text-align');
        if (!textAlign) {
            textAlign = 'center';
        }

        switch (textAlign) {
            case 'center':
                resizable.style.right = 'unset';
                resizable.style.left = (parentW - paddingRight - resizableW) / 2 + 'px';
                break;
            case 'right':
                if (percentW < 1) {
                    resizable.style.right = paddingRight - (resizableW * percentWDiff) / 2 + 'px';
                    resizable.style.left = 'unset';
                } else {
                    resizable.style.left = 'unset';
                    resizable.style.right = paddingRight + 'px';
                }
                break;
            case 'left':
                if (percentW < 1) {
                    resizable.style.right = paddingLeft - (resizableW * percentWDiff) / 2 + 'px';
                    resizable.style.left = 'unset';
                } else {
                    resizable.style.left = 'unset';
                    resizable.style.right = paddingLeft + 'px';
                }
                break;
        }
        resizable.style.top = (targetH - resizableH - moveUp) / 2 + 'px';
    });
};

const sectionJackpotComponents = {
    // 'PROGRESSIVE': null,
    MYSTERY: MysteryJackpot,
    'CLOVER CHANCE': MysteryJackpot,
    // 'NEW_GAMES': null,
    // 'TOP_GAMES': null,
    'BELL LINK': BellLinkJackpot,
    'VIP BELL LINK': VipBellLinkJackpot,
    'HIGH CASH': HighCashJackpot,
    'VIP HIGH CASH': VipHighCashJackpot,
    INSTANTLINK: InstantLinkJackpot,
    // 'FRUITS': null,
    // 'CLASSIC': null,
    // 'LEGENDARY': null,
    // 'ALL_GAMES': null,
};

const gameJackpotComponents = {
    // RORSlot: SingleProgressive,
    // TSHSlot: SingleProgressive,
    // FSHSlot: SingleProgressive,
    // BHSlot: SingleProgressive,
    // VGSlot: SingleProgressive,
};

const onlyMaxBetGames = ['RORSlot', 'TSHSlot', 'FSHSlot', 'BHSlot', 'VGSlot'];

export const isOnlyMaxBetGame = (gameKey: string): boolean => onlyMaxBetGames.includes(gameKey);

export const getGameJackpotComponents = (
    gameKey: string,
): {
    jackpotComponent?: React.FunctionComponent<any>;
    jackpotComponentRenderInside?: 'tags' | 'block';
} => {
    if (gameJackpotComponents.hasOwnProperty(gameKey)) {
        type ObjectKey = keyof typeof gameJackpotComponents;
        return {
            jackpotComponent: gameJackpotComponents[gameKey as ObjectKey],
            jackpotComponentRenderInside: 'block',
        };
    } else if (gameKey === X_RIDE_MP) {
        return {
            jackpotComponent: CrashGameLabel,
            jackpotComponentRenderInside: 'tags',
        };
    } else {
        return {};
    }
};

export const getSectionJackpotComponent = (
    jackpotName: string | null,
    sectionName: string,
): { jackpotComponent?: React.FunctionComponent<{ group?: string }> } => {
    if (jackpotName && sectionJackpotComponents.hasOwnProperty(jackpotName.toUpperCase())) {
        type ObjectKey = keyof typeof sectionJackpotComponents;
        return {
            jackpotComponent: sectionJackpotComponents[jackpotName.toUpperCase() as ObjectKey],
        };
    } else if (sectionName.toUpperCase().indexOf('TOURNAMENT') !== -1) {
        return {
            jackpotComponent: Tournament,
        };
    } else if (sectionName.toUpperCase().indexOf('PROGRESSIVE') !== -1) {
        return {
            jackpotComponent: StaticImageBanner,
        };
    } else {
        return {};
    }
};

export const addBuildHash = (value: string) => `${value}?hash=${WEBPACK_HASH}`;

export const getSoundDuration = (soundName: string): number => {
    const selectedSound = soundSprites?.[soundName as keyof SoundSpriteDefinitions];
    if (selectedSound && selectedSound.length > 1) {
        return selectedSound[1];
    } else {
        return 0;
    }
};

export const getBrandedTournamentImages = (brand: string, lang: string, host: string): string => {
    const brandsMap = BrandedTournamentImagesMap.hasOwnProperty(brand)
        ? BrandedTournamentImagesMap[brand as keyof typeof BrandedTournamentImagesMap]
        : BrandedTournamentImagesMap.default;
    const image: string = brandsMap.hasOwnProperty(lang)
        ? brandsMap[lang as keyof typeof brandsMap]
        : brandsMap.default;
    return addBuildHash(`${host}${image}`);
};

export const getBrandedStaticImages = (group: string, brand: string, lang: string, host: string): string => {
    const imagesMap = BrandedStaticImagesMap[group.toLowerCase() as keyof typeof BrandedStaticImagesMap];
    const brandsMap = imagesMap.hasOwnProperty(brand) ? imagesMap[brand as keyof typeof imagesMap] : imagesMap.default;
    const image: string = brandsMap.hasOwnProperty(lang)
        ? brandsMap[lang as keyof typeof brandsMap]
        : brandsMap.default;
    return addBuildHash(`${host}${image}`);
};

export const shouldShowScrollers = (
    el: HTMLDivElement,
    scrollPosition: number,
): { shouldShowLeft: boolean; shouldShowRight: boolean } => {
    const scrolledLeft = scrollPosition;
    const isScrolledLeft = !!scrolledLeft && scrolledLeft > 0;
    const isAtMaxRightScroll = Math.ceil((scrollPosition || 0) + (el?.offsetWidth || 0)) >= (el?.scrollWidth || 0);

    return {
        shouldShowLeft: isScrolledLeft,
        shouldShowRight: !isAtMaxRightScroll,
    };
};

const getImageType = (settings: ISettings, singleView: boolean): string => {
    if (settings.mode === 'desktop') {
        if (singleView) {
            return 'desktop_section';
        } else {
            return 'desktop_lobby';
        }
    } else {
        return 'mobile';
    }
};

export const getBackgroundImagePathForSection = (
    settings: ISettings,
    sectionName: string,
    singleView: boolean,
): string => {
    return `url(${settings.host}${addBuildHash(
        `/assets/portal/backgrounds/${sectionName}/${getImageType(settings, singleView)}.webp`,
    )})`;
};

export const getSectionBackgroundColor = (sectionName: string): { backgroundColor?: string } => {
    return bgSectionColor[sectionName as keyof typeof bgSectionColor] !== undefined
        ? { backgroundColor: bgSectionColor[sectionName as keyof typeof bgSectionColor] }
        : {};
};

export const getBackgroundSettings = (
    settings: ISettings,
    sectionName: string,
    singleView: boolean,
): [
    {
        backgroundImage?: string;
        backgroundSize?: string;
        backgroundColor?: string;
    },
    boolean,
] => {
    const hasBackground = sectionsWithBgImages.includes(sectionName) || sectionName.indexOf('tournament') !== -1;
    const retVal = {
        ...(hasBackground
            ? {
                  backgroundImage: getBackgroundImagePathForSection(
                      settings,
                      sectionName.indexOf('tournament') === -1 ? sectionName : 'tournament',
                      singleView,
                  ),
              }
            : {}),
        ...getSectionBackgroundColor(sectionName.indexOf('tournament') === -1 ? sectionName : 'tournament'),
    };
    return [retVal, hasBackground];
};

export const formatDate = (d: Date): string => {
    //// Date.getMonth() returns the month number starting from 0 for JAN ////
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

export const pipe = (...fns: Array<(v: any) => any>) => (x: any) => fns.reduce((v: any, f) => f(v), x);

export const numberWithThousandSeparator = (x: number, fixed: number = 2, separator: string = ' ') =>
    x.toFixed(fixed).replace(/\B(?=(\d{3})+(?!\d))/g, separator);

export const getLocalStorageItem = (key: string, currentGameKey: string): string => {
    try {
        return localStorage?.getItem(key) || `[${currentGameKey ? '"' + currentGameKey + '"' : ''}]`;
    } catch (err) {
        return `[${currentGameKey ? '"' + currentGameKey + '"' : ''}]`;
    }
};
